import { useState, useRef, useCallback, useEffect, useMemo } from 'react';

function useFlexibleWidth({ containerRef, emblaApi, flexibleWidth, itemsToScroll, itemGap, showItemPreview, }) {
    const [calculatedItemsInView, setCalculatedItemsInView] = useState(itemsToScroll);
    const [isCalculating, setIsCalculating] = useState(false);
    const resizeObserverRef = useRef(null);
    const itemWidthsRef = useRef([]);
    // Convert itemGap token to pixel value
    const itemGapPx = itemGap === "md" ? 8 : 16;
    const calculateOptimalItemsInView = useCallback(() => {
        if (!flexibleWidth || !containerRef.current || !emblaApi) {
            return itemsToScroll;
        }
        setIsCalculating(true);
        const container = containerRef.current;
        const containerWidth = container.offsetWidth;
        // Get all slide elements
        const slideElements = container.querySelectorAll("[data-carousel-slide]");
        if (slideElements.length === 0) {
            setIsCalculating(false);
            return itemsToScroll;
        }
        // Measure actual widths of items
        const itemWidths = [];
        slideElements.forEach((slide) => {
            const slideElement = slide;
            // Reset width to auto to measure natural width
            const originalWidth = slideElement.style.width;
            slideElement.style.width = "auto";
            slideElement.style.minWidth = "0";
            slideElement.style.flexShrink = "0";
            const rect = slideElement.getBoundingClientRect();
            itemWidths.push(rect.width);
            // Restore original width
            slideElement.style.width = originalWidth;
        });
        itemWidthsRef.current = itemWidths;
        // Calculate how many items can fit
        let totalWidth = 0;
        let itemCount = 0;
        const previewWidth = showItemPreview ? 48 : 0; // Reserve space for preview
        const availableWidth = containerWidth - previewWidth;
        for (let i = 0; i < itemWidths.length; i++) {
            const itemWidth = itemWidths[i];
            const gapWidth = i > 0 ? itemGapPx : 0;
            const requiredWidth = totalWidth + gapWidth + itemWidth;
            if (requiredWidth <= availableWidth) {
                totalWidth = requiredWidth;
                itemCount++;
            }
            else {
                break;
            }
        }
        // Ensure we meet minimum requirement
        const optimalCount = Math.max(itemsToScroll, itemCount);
        setIsCalculating(false);
        return optimalCount;
    }, [
        flexibleWidth,
        containerRef,
        emblaApi,
        itemsToScroll,
        itemGapPx,
        showItemPreview,
    ]);
    // Set up ResizeObserver to recalculate on container size changes
    useEffect(() => {
        if (!flexibleWidth || !containerRef.current) {
            return;
        }
        const container = containerRef.current;
        resizeObserverRef.current = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === container) {
                    // Debounce recalculation
                    const timeoutId = setTimeout(() => {
                        const newItemsInView = calculateOptimalItemsInView();
                        setCalculatedItemsInView(newItemsInView);
                    }, 100);
                    return () => clearTimeout(timeoutId);
                }
            }
        });
        resizeObserverRef.current.observe(container);
        return () => {
            if (resizeObserverRef.current) {
                resizeObserverRef.current.disconnect();
            }
        };
    }, [flexibleWidth, containerRef, calculateOptimalItemsInView]);
    // Initial calculation when component mounts or key dependencies change
    useEffect(() => {
        if (flexibleWidth && emblaApi && containerRef.current) {
            // Wait for embla to initialize and slides to render
            const timeoutId = setTimeout(() => {
                const newItemsInView = calculateOptimalItemsInView();
                setCalculatedItemsInView(newItemsInView);
            }, 50);
            return () => clearTimeout(timeoutId);
        }
        else {
            setCalculatedItemsInView(itemsToScroll);
        }
    }, [
        flexibleWidth,
        emblaApi,
        calculateOptimalItemsInView,
        itemsToScroll,
        containerRef,
    ]);
    // Return calculated value or fallback to original itemsInView
    const result = useMemo(() => {
        if (!flexibleWidth) {
            return {
                calculatedItemsInView: itemsToScroll,
                isCalculating: false,
            };
        }
        return {
            calculatedItemsInView,
            isCalculating,
        };
    }, [flexibleWidth, calculatedItemsInView, isCalculating, itemsToScroll]);
    return result;
}

export { useFlexibleWidth };
